import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const textInputSizes = ['xs', 'sm'] as const;
export type TextInputSize = (typeof textInputSizes)[number];

const sizeClasses: Record<TextInputSize, string> = {
  xs: 'text-xs px-2 py-1.5',
  sm: 'text-sm px-2.5 py-2',
};

// `size` collides with the native `<input size="N">` attribute, so we omit
// it from the underlying props and re-introduce our own size scale.
type NativeInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

export interface TextInputProps extends NativeInputProps {
  size?: TextInputSize;
  /** Visually distinguishes invalid input with a red border + focus ring. */
  invalid?: boolean;
  className?: ClassName;
}

/**
 * Standard text input with the app's border/focus treatment. Covers
 * date, time, and plain-text inputs equivalently — the underlying
 * `type` is passed through.
 *
 * Used in: trip-create form, schedule form (date pickers, time
 * pickers, duration), trip-date edit, en-route note editor.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ size = 'xs', invalid = false, className, type = 'text', ...rest }, ref) => (
    <input
      ref={ref}
      type={type}
      className={twx(
        'w-full bg-white border rounded-lg outline-none transition-[colors,box-shadow] duration-150',
        'focus:ring-2',
        invalid
          ? 'border-error-border focus:border-error-main focus:ring-error-main/20'
          : 'border-content-border focus:border-secondary-emphasized focus:ring-secondary-emphasized/20',
        sizeClasses[size],
        className,
      )}
      {...rest}
    />
  ),
);
TextInput.displayName = 'TextInput';

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  size?: TextInputSize;
  invalid?: boolean;
  /** Grow the textarea's height to fit its content (no scroll bar). The
   * height transitions smoothly so each new line feels like the input is
   * "breathing" with the text. */
  autoGrow?: boolean;
  className?: ClassName;
}

/** Same visual contract as TextInput but for `<textarea>`. */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ size = 'xs', invalid = false, autoGrow = false, className, onInput, ...rest }, ref) => {
    // When `autoGrow` is set, we measure scrollHeight after every input and
    // set the element's height inline. The transition-[height] class
    // animates the grow/shrink so it doesn't snap.
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.RefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );
    const resize = useCallback(() => {
      const el = internalRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, []);
    // Resize on mount + when the controlled value changes externally.
    // biome-ignore lint/correctness/useExhaustiveDependencies: triggered by external value updates
    useEffect(() => {
      if (autoGrow) resize();
    }, [autoGrow, resize, rest.value]);
    return (
      <textarea
        ref={setRefs}
        onInput={(e) => {
          if (autoGrow) resize();
          onInput?.(e);
        }}
        className={twx(
          'w-full bg-white border rounded-lg outline-none resize-none transition-[colors,box-shadow,height] duration-150',
          'focus:ring-2',
          invalid
            ? 'border-error-border focus:border-error-main focus:ring-error-main/20'
            : 'border-content-border focus:border-secondary-emphasized focus:ring-secondary-emphasized/20',
          sizeClasses[size],
          autoGrow && 'overflow-hidden',
          className,
        )}
        {...rest}
      />
    );
  },
);
TextArea.displayName = 'TextArea';
